import type {
  AppState,
  ItemStatusView,
  KnowledgeItem,
  KnowledgeType,
  LearningProgress,
  ParentMetrics,
  ReviewEvent,
  ReviewResult
} from '../types';

const DAY = 24 * 60 * 60 * 1000;

export function makeId(prefix: string) {
  const value = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${value}`;
}

export function addDays(date: string | Date, amount: number) {
  const value = new Date(date);
  value.setDate(value.getDate() + amount);
  return value.toISOString();
}

export function addMinutes(date: string | Date, amount: number) {
  return new Date(new Date(date).getTime() + amount * 60 * 1000).toISOString();
}

export function isSameLocalDay(left: string | Date, right: string | Date) {
  const a = new Date(left);
  const b = new Date(right);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function endOfToday() {
  const value = new Date();
  value.setHours(23, 59, 59, 999);
  return value.toISOString();
}

export function isDue(progress: LearningProgress | undefined, now = new Date()) {
  if (!progress) return true;
  if (progress.masteredAt) return false;
  return new Date(progress.nextReviewAt).getTime() <= now.getTime();
}

export function getChildProgress(state: AppState, childId: string, itemId: string) {
  return state.progress.find((entry) => entry.childId === childId && entry.itemId === itemId);
}

export function getLearningPlanIds(state: AppState, childId?: string) {
  if (childId && state.learningPlansByChild) {
    return new Set(state.learningPlansByChild[childId] ?? []);
  }
  if (state.learningPlansByChild) {
    return new Set(Object.values(state.learningPlansByChild).flat());
  }
  return new Set(state.learningPlan ?? []);
}

export function getLearningPlanChildIds(state: AppState, itemId: string) {
  return state.children
    .filter((child) => getLearningPlanIds(state, child.id).has(itemId))
    .map((child) => child.id);
}

export function getPlanCount(state: AppState, type?: KnowledgeType, childId?: string) {
  const planIds = getLearningPlanIds(state, childId);
  return state.knowledge.filter((item) => (
    planIds.has(item.id) && (!type || item.type === type)
  )).length;
}

export function getItemStatus(state: AppState, itemId: string): ItemStatusView {
  const childStates = state.children.map((child) => {
    const childProgress = getChildProgress(state, child.id, itemId);
    const status = !childProgress
      ? 'unlearned'
      : childProgress.masteredAt
        ? 'mastered'
        : 'learning';
    return { child, progress: childProgress, status } as const;
  });

  let status: ItemStatusView['status'] = 'learning';
  if (childStates.length === 0 || childStates.every((entry) => entry.status === 'unlearned')) {
    status = 'unlearned';
  } else if (childStates.every((entry) => entry.status === 'mastered')) {
    status = 'mastered';
  }

  return { status, childStates };
}

export function getLearningQueue(state: AppState, childId: string, type: KnowledgeType) {
  const now = new Date();
  const planIds = getLearningPlanIds(state, childId);
  return state.knowledge
    .filter((item) => item.type === type && planIds.has(item.id))
    .map((item) => {
      const progress = getChildProgress(state, childId, item.id);
      return { item, progress, due: isDue(progress, now) };
    })
    .filter((entry) => entry.due)
    .sort((left, right) => {
      const leftLearned = Boolean(left.progress);
      const rightLearned = Boolean(right.progress);
      if (leftLearned !== rightLearned) return leftLearned ? -1 : 1;
      if (left.progress && right.progress) {
        return new Date(left.progress.nextReviewAt).getTime() - new Date(right.progress.nextReviewAt).getTime();
      }
      return new Date(left.item.createdAt).getTime() - new Date(right.item.createdAt).getTime();
    });
}

export function getDueCount(state: AppState, childId: string, type?: KnowledgeType) {
  const types: KnowledgeType[] = type ? [type] : ['character', 'poem', 'word'];
  return types.reduce((count, currentType) => count + getLearningQueue(state, childId, currentType).length, 0);
}

export function getMemoryLabel(progress: LearningProgress | undefined, intervals: number[]) {
  if (!progress) {
    return {
      eyebrow: '第一次记忆',
      detail: '先认真看一看，再选择认识或忘记',
      step: 0,
      total: intervals.length
    };
  }
  if (progress.masteredAt) {
    return {
      eyebrow: '已完成全部记忆',
      detail: '这颗知识种子已经稳稳长大啦',
      step: intervals.length,
      total: intervals.length
    };
  }
  const step = Math.min(progress.stage + 1, intervals.length);
  return {
    eyebrow: `第 ${step} 次记忆`,
    detail: `上次选择：${progress.correctCount >= progress.forgotCount ? '记得不错' : '需要再巩固'}`,
    step,
    total: intervals.length
  };
}

export function performReview(
  state: AppState,
  childId: string,
  itemId: string,
  result: ReviewResult
): { state: AppState; event: ReviewEvent; item: KnowledgeItem } {
  const item = state.knowledge.find((entry) => entry.id === itemId);
  if (!item) throw new Error('知识条目不存在');
  const child = state.children.find((entry) => entry.id === childId);
  if (!child) throw new Error('儿童不存在');

  const existing = getChildProgress(state, childId, itemId);
  const intervals = state.settings.intervals[item.type];
  const rewards = state.settings.rewards[item.type];
  const reviewedAt = new Date().toISOString();
  const stageBefore = existing?.stage ?? 0;
  const known = result === 'known';
  const stageAfter = known
    ? Math.min(intervals.length, stageBefore + 1)
    : Math.max(0, stageBefore - 1);
  const completed = known && !existing?.masteredAt && stageAfter >= intervals.length;
  const starsDelta = (known ? rewards.known : rewards.forgot) + (completed ? rewards.mastered : 0);
  const nextReviewAt = completed
    ? addDays(reviewedAt, 36500)
    : addDays(reviewedAt, intervals[Math.min(stageAfter, intervals.length - 1)] ?? 0);
  const event: ReviewEvent = {
    id: makeId('review'),
    childId,
    itemId,
    result,
    reviewedAt,
    stageBefore,
    stageAfter,
    starsDelta,
    completed
  };

  const nextProgress: LearningProgress = existing
    ? {
        ...existing,
        stage: stageAfter,
        masteredAt: completed ? reviewedAt : existing.masteredAt,
        nextReviewAt: completed ? nextReviewAt : result === 'forgot' ? addMinutes(reviewedAt, 10) : nextReviewAt,
        attempts: existing.attempts + 1,
        correctCount: existing.correctCount + (known ? 1 : 0),
        forgotCount: existing.forgotCount + (known ? 0 : 1),
        history: [event, ...existing.history].slice(0, 200)
      }
    : {
        id: makeId('progress'),
        childId,
        itemId,
        stage: stageAfter,
        learnedAt: reviewedAt,
        masteredAt: completed ? reviewedAt : undefined,
        nextReviewAt: result === 'forgot' ? addMinutes(reviewedAt, 10) : nextReviewAt,
        attempts: 1,
        correctCount: known ? 1 : 0,
        forgotCount: known ? 0 : 1,
        history: [event]
      };

  const progress = existing
    ? state.progress.map((entry) => (entry.id === existing.id ? nextProgress : entry))
    : [...state.progress, nextProgress];
  const children = state.children.map((entry) =>
    entry.id === childId ? { ...entry, stars: entry.stars + starsDelta } : entry
  );

  return {
    state: {
      ...state,
      children,
      progress,
      updatedAt: new Date().toISOString()
    },
    event,
    item
  };
}

export function getParentMetrics(state: AppState, childId: string): ParentMetrics {
  const childProgress = state.progress.filter((entry) => entry.childId === childId);
  const reviewEvents = childProgress
    .flatMap((entry) => entry.history)
    .filter((event) => isSameLocalDay(event.reviewedAt, new Date()));
  const started = childProgress.length;
  const mastered = childProgress.filter((entry) => entry.masteredAt).length;
  const difficultItems = childProgress
    .filter((entry) => entry.forgotCount > 0)
    .map((entry) => {
      const item = state.knowledge.find((knowledgeItem) => knowledgeItem.id === entry.itemId)!;
      return {
        item,
        forgotCount: entry.forgotCount,
        attempts: entry.attempts,
        rate: entry.attempts ? Math.round((entry.forgotCount / entry.attempts) * 100) : 0
      };
    })
    .filter((entry) => entry.item)
    .sort((left, right) => right.forgotCount - left.forgotCount || right.rate - left.rate)
    .slice(0, 5);

  return {
    reviewCount: reviewEvents.length,
    masteryRate: started ? Math.round((mastered / started) * 100) : 0,
    forgottenCount: reviewEvents.filter((event) => event.result === 'forgot').length,
    difficultItems,
    dueCount: getDueCount(state, childId)
  };
}

export function getTodayReviewEvents(state: AppState, childId: string) {
  return state.progress
    .filter((entry) => entry.childId === childId)
    .flatMap((entry) => entry.history)
    .filter((event) => isSameLocalDay(event.reviewedAt, new Date()));
}

export function getWeeklyActivity(state: AppState, childId: string) {
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - index));
    const reviews = state.progress
      .filter((entry) => entry.childId === childId)
      .flatMap((entry) => entry.history)
      .filter((event) => isSameLocalDay(event.reviewedAt, date));
    return {
      date,
      label: ['日', '一', '二', '三', '四', '五', '六'][date.getDay()],
      count: reviews.length,
      correct: reviews.filter((event) => event.result === 'known').length
    };
  });
}

export function formatRelativeReview(date: string) {
  const target = new Date(date);
  const today = new Date();
  const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const offset = Math.round((targetDay - todayDay) / DAY);
  if (offset < 0) return `已逾期 ${Math.abs(offset)} 天`;
  if (offset === 0) return '今天复习';
  if (offset === 1) return '明天复习';
  return `${offset} 天后复习`;
}

export function formatDateTime(date: string) {
  return new Intl.DateTimeFormat('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(date));
}

export function formatBirthMonth(value: string) {
  if (!value) return '未填写';
  const [year, month] = value.split('-');
  return `${year} 年 ${Number(month)} 月`;
}

export function getAgeText(birthMonth: string) {
  if (!birthMonth) return '';
  const birth = new Date(`${birthMonth}-01T00:00:00`);
  const nowDate = new Date();
  let months = (nowDate.getFullYear() - birth.getFullYear()) * 12 + nowDate.getMonth() - birth.getMonth();
  if (months < 0) months = 0;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (years <= 0) return `${remainingMonths} 个月`;
  return remainingMonths ? `${years} 岁 ${remainingMonths} 个月` : `${years} 岁`;
}

export function calculateTotalMastered(state: AppState, childId: string) {
  const learned = state.progress.filter((entry) => entry.childId === childId && entry.masteredAt);
  return {
    character: learned.filter((entry) => state.knowledge.find((item) => item.id === entry.itemId)?.type === 'character').length,
    poem: learned.filter((entry) => state.knowledge.find((item) => item.id === entry.itemId)?.type === 'poem').length,
    word: learned.filter((entry) => state.knowledge.find((item) => item.id === entry.itemId)?.type === 'word').length
  };
}
